import * as React from "react";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { ReducersModal } from "utils/modals";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { getCarCompanyList } from "modules/company/action";

interface Props {
  labelProp: string;
  disabled?: boolean;

  /**
   * Existing VehicleDetails.carCompany
   * is a CompanyDetails object.
   */
  selectedCompany?: any;

  onSelectCompany: (company: any) => void;
}

const getCompanyId = (company: any) => {
  const id =
    company?.id ||
    company?._id ||
    company?.companyId ||
    company?.brandId ||
    "";

  // CompanyDetails defaults to id: -1 when empty
  if (id === -1 || id === "-1" || id === "") {
    return "";
  }

  return String(id);
};

const getCompanyName = (company: any) =>
  company?.name ||
  company?.companyName ||
  company?.brandName ||
  "";

const isSameCompany = (a: any, b: any) => {
  const aId = getCompanyId(a);
  const bId = getCompanyId(b);

  if (aId && bId) {
    return aId === bId;
  }

  const aName = getCompanyName(a);
  const bName = getCompanyName(b);

  return Boolean(aName && bName && aName === bName);
};

export default function CompanyDropDown({
  labelProp,
  disabled = false,
  selectedCompany = null,
  onSelectCompany,
}: Props) {
  const dispatch: any = useDispatch();

  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [inputValue, setInputValue] = React.useState("");

  const { carCompanies } = useSelector(
    (state: ReducersModal) => state.CarCompanyReducer
  );

  const options = React.useMemo(
    () => carCompanies?.result || [],
    [carCompanies?.result]
  );

  /**
   * Keep the text field in sync with the selected company
   * (needed for EDIT pre-select). Search stays separate so
   * we don't overwrite the display with an empty query string.
   */
  React.useEffect(() => {
    const name = getCompanyName(selectedCompany);
    const id = getCompanyId(selectedCompany);

    if (name) {
      setInputValue(name);
    } else if (!id) {
      setInputValue("");
    }
  }, [selectedCompany]);

  React.useEffect(() => {
    dispatch(
      getCarCompanyList(
        () => {},
        {
          search,
        }
      )
    );
  }, [search, dispatch]);

  /**
   * Prefer the matching option from the API list.
   * If pagination hasn't returned it yet, fall back to
   * the selectedCompany object so EDIT still shows a value.
   */
  const selectedOption = React.useMemo(() => {
    const selectedId = getCompanyId(selectedCompany);
    const selectedName = getCompanyName(selectedCompany);

    if (!selectedId && !selectedName) {
      return null;
    }

    const matched =
      options.find((company: any) =>
        isSameCompany(company, selectedCompany)
      ) || null;

    return matched || selectedCompany;
  }, [options, selectedCompany]);

  /**
   * Ensure the selected company appears in options even when
   * it is not on the current paginated page.
   */
  const mergedOptions = React.useMemo(() => {
    if (!selectedOption) {
      return options;
    }

    const alreadyIncluded = options.some((company: any) =>
      isSameCompany(company, selectedOption)
    );

    if (alreadyIncluded) {
      return options;
    }

    return [selectedOption, ...options];
  }, [options, selectedOption]);

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={selectedOption}
      options={mergedOptions}
      loading={!options.length && !selectedOption}
      isOptionEqualToValue={(option, value) =>
        isSameCompany(option, value)
      }
      getOptionLabel={(option: any) => getCompanyName(option)}
      onChange={(_, value) => {
        onSelectCompany(value);
      }}
      inputValue={inputValue}
      onInputChange={(_, value, reason) => {
        setInputValue(value);

        if (reason === "input") {
          setSearch(value);
        }

        if (reason === "clear") {
          setSearch("");
          onSelectCompany(null);
        }
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        return (
          <Box key={key} component="li" {...optionProps}>
            {getCompanyName(option)}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={labelProp}
        />
      )}
    />
  );
}
